import { useCallback, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import * as Sentry from '@sentry/nextjs';
import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import IcRadioFill from 'src/assets/images/icRadioFill.svg';
import IcRadioOff from 'src/assets/images/icRadioOff.svg';
import useRegister from 'src/components/AzarOnly/Login/useRegister';
import Button from 'src/components/Button';
import { ModalContent, ModalFooter as ModalFooterBase, ModalLoading, ModalTitle as ModalTitleBase, ModalTitleContainer, ModalTitleDescription as ModalTitleDescriptionBase } from 'src/components/ModalLayout';
import { FALLBACK_SIGNUP_SETTINGS, primaryLanguageCodeAtom, signUpSettingsLoadableAtom } from 'src/stores/register/atoms';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { pushEvent } from 'src/utils/event-gateway';
const ModalTitle = styled(ModalTitleBase)`
  font-size: 30px;
  line-height: 40px;
`;
const ModalTitleDescription = styled(ModalTitleDescriptionBase)`
  font-size: 14px;
  line-height: 18px;
  color: ${({
  theme
}) => theme.color.content9};

  ${({
  theme
}) => theme.screenSize.mobile} {
    font-size: 16px;
    line-height: normal;
    color: ${({
  theme
}) => theme.color.content11};
  }
`;
const ModalFooter = styled(ModalFooterBase)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;
const LanguageList = styled.div`
  display: flex;
  flex-direction: column;
`;
const LanguageOption = styled.button`
  all: unset;
  cursor: pointer;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 0;
  overflow: clip;
`;
const LanguageName = styled.span`
  flex: 1;
  font-size: 16px;
  line-height: 21px;
  font-weight: 400;
  color: ${({
  theme
}) => theme.color.content12};
`;
const RadioIconCss = css`
  width: 28px;
  height: 28px;
  flex-shrink: 0;
`;
const ActiveRadio = styled(IcRadioFill)`
  ${RadioIconCss}
  color: ${({
  theme
}) => theme.color.primary1};
`;
const InActiveRadio = styled(IcRadioOff)`
  ${RadioIconCss}
  color: ${({
  theme
}) => theme.color.content5};
`;
const PrimaryLanguageStep = () => {
  const {
    t
  } = useTranslation();
  const {
    tryRegister
  } = useRegister();
  const setPrimaryLanguageCode = useSetAtom(primaryLanguageCodeAtom);
  const signUpSettingsLoadable = useAtomValue(signUpSettingsLoadableAtom);
  const signUpSettings = signUpSettingsLoadable.state === 'hasData' ? signUpSettingsLoadable.data : FALLBACK_SIGNUP_SETTINGS;
  const languageOptions = signUpSettings.conversationLanguageSettings?.languageOptions ?? [];
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const handleNext = useCallback(async () => {
    if (!selectedCode) return;
    setPrimaryLanguageCode(selectedCode);
    pushEvent({
      eventType: EVENT_TYPE.SIGN_UP,
      eventName: EVENT_NAME.SIGNUP__CLICK_NEXT_MAIN_LANGUAGE,
      eventParams: {
        action_category: 'click',
        tab: 'signup',
        page: 'main_language',
        target: 'next',
        result: selectedCode
      }
    });
    await tryRegister();
  }, [selectedCode, setPrimaryLanguageCode, tryRegister]);
  useEffect(() => {
    pushEvent({
      eventType: EVENT_TYPE.SIGN_UP,
      eventName: EVENT_NAME.SIGNUP__IMPRESSION_MAIN_LANGUAGE,
      eventParams: {
        action_category: 'impression',
        tab: 'signup',
        page: 'main_language',
        target: 'main_language'
      }
    });
  }, []);

  // 비동기 설정 로딩 결과에 따라 이 스텝을 건너뛸지 결정한다.
  // hasError이거나, hasData이지만 conversationLanguageSettings가 없으면 tryRegister()로 다음 스텝으로 진행한다.
  useEffect(() => {
    if (signUpSettingsLoadable.state === 'loading') return;
    if (!signUpSettings.conversationLanguageSettings) {
      if (signUpSettingsLoadable.state === 'hasData') {
        Sentry.captureMessage('Entered PrimaryLanguageStep without conversationLanguageSettings');
      }
      tryRegister();
    }
  }, [tryRegister, signUpSettingsLoadable.state, signUpSettings.conversationLanguageSettings]);
  if (signUpSettingsLoadable.state === 'loading') {
    return <ModalLoading />;
  }
  if (!signUpSettings.conversationLanguageSettings) {
    return null;
  }
  return <>
      <ModalTitleContainer data-sentry-element="ModalTitleContainer" data-sentry-source-file="index.tsx">
        <ModalTitle data-testid='LoginModal-PrimaryLanguageStep' data-sentry-element="ModalTitle" data-sentry-source-file="index.tsx">
          {t('signup.1st_lang_title')}
        </ModalTitle>
        <ModalTitleDescription data-sentry-element="ModalTitleDescription" data-sentry-source-file="index.tsx">{t('signup.1st_lang_desc')}</ModalTitleDescription>
      </ModalTitleContainer>
      <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="index.tsx">
        <LanguageList data-sentry-element="LanguageList" data-sentry-source-file="index.tsx">
          {languageOptions.map(({
          code,
          displayName
        }) => {
          const isActive = selectedCode === code;
          return <LanguageOption key={code} onClick={() => setSelectedCode(code)} data-testid='PrimaryLanguageStep-option' data-isactive={isActive}>
                {isActive ? <ActiveRadio /> : <InActiveRadio />}
                <LanguageName>{displayName}</LanguageName>
              </LanguageOption>;
        })}
        </LanguageList>
      </ModalContent>
      <ModalFooter data-sentry-element="ModalFooter" data-sentry-source-file="index.tsx">
        <Button id='sign-up-primary-language-next' disabled={!selectedCode} onClick={handleNext} data-testid='PrimaryLanguageStep-button-next' data-sentry-element="Button" data-sentry-source-file="index.tsx">
          {t('SIGNUP__NEXT')}
        </Button>
      </ModalFooter>
    </>;
};
export default PrimaryLanguageStep;